import React, { useMemo, useState, useRef, useEffect } from 'react';
import Swal from 'sweetalert2';
import MacrosModal from '../../MacrosModal/MacrosModal.jsx';
import { useSelector } from 'react-redux';
import sendIcon from '../../../../../assets/img/send.svg';
import dismissIcon from '../../../../../assets/img/dismiss.svg';
import flashIcon from '../../../../../assets/img/flash.svg';
import addIcon from '../../../../../assets/img/add.svg';
import { IconButton, InputAdornment, Stack, TextField } from '@mui/material';
import useChatFiles from '../../../../../hooks/useChatFiles';
import ChatSectionFilePreview from './ChatSectionFilePreview.jsx';
import { getAttachmentType, getImageDimensions } from '../../../../../global/Helpers.js';
import { useSendInternalMessageMutation, useSendStreamMessageMutation } from '../../../../../api/messagesApi.js';
import { isNil } from 'lodash';
import { selectedUserIdSelector } from '../../../../../selectors/userSelectors.js';
import { useGetUserInfoQuery } from '../../../../../api/userApi.js';
import { useUploadFileMutation } from '../../../../../api/filesApi.js';
import { isTaxExpertSelector } from '../../../../../selectors/authSelectors.js';
import { captureException } from '@sentry/react';
import BookkeeperMentions from '../../../../Common/BookkeeperMentions.jsx';
import { useBookkeeperMentionOptions } from '../../../../../hooks/useBookkeeperMentionOptions.js';

const ChatSectionFooter = () => {
  const [message, setMessage] = useState('');
  const [isMessageSending, setIsMessageSending] = useState(false);
  const [isMentionOpen, setIsMentionOpen] = useState(false);
  const [isMacroModalOpen, setIsMacroModalOpen] = useState(false);

  const textFieldInputRef = useRef(null);

  const [sendInternalMessage] = useSendInternalMessageMutation();
  const [uploadFile] = useUploadFileMutation();
  const [sendMessage] = useSendStreamMessageMutation();

  const selectedUserId = useSelector(selectedUserIdSelector);
  const isTaxExpert = useSelector(isTaxExpertSelector);

  const { stagedFile, setStagedFile, openFileSelector, onClose, clear, plainFiles, filesContent } = useChatFiles();
  const { data: userInfo, isLoading: isUserInfoLoading } = useGetUserInfoQuery(selectedUserId);
  const { data: bookkeeperOptions } = useBookkeeperMentionOptions();

  const isMessageEmpty = message.trim().length === 0;
  const isSendDisabled = isMessageSending || (isMessageEmpty && isTaxExpert);

  /** IDs of bookkeepers who have been tagged/mentioned */
  const mentionedBookkeeperIds = useMemo(() => {
    return bookkeeperOptions.filter((option) => message.includes(`@${option.label}`)).map((option) => option.value);
  }, [message, bookkeeperOptions]);

  const handleSetMessage = (value) => {
    const lastChar = value.slice(-1);
    const charBeforeLast = value.slice(-2, -1);
    // Only show tag menu if the last character is an @ and the character before it is a space or empty.
    // This is to prevent the tag menu from showing when the user is typing out an email address.
    if (lastChar === '@' && (charBeforeLast === '' || charBeforeLast === ' ')) {
      setIsMentionOpen(true);
    } else {
      setIsMentionOpen(false);
    }
    setMessage(value);
  };

  /** Focus on the text input when the mention dropdown is closed */
  useEffect(() => {
    if (!isMentionOpen) {
      textFieldInputRef.current.focus();
    }
  }, [isMentionOpen]);

  /** File to upload with this message */
  const fileToUpload = useMemo(() => {
    const fileType = plainFiles[0]?.type;
    const fileContent = filesContent[0]?.content;
    const filename = plainFiles[0]?.name;
    if (!fileType || !fileContent || !filename) {
      return null;
    }
    return { type: fileType, data: fileContent?.split(',')[1], imageUrl: fileContent, filename };
  }, [plainFiles, filesContent]);

  const handleSendChatMessage = async (e, phone) => {
    setIsMessageSending(true);
    e.preventDefault();
    const memberPhone = userInfo.phone;
    if (String(memberPhone) !== String(phone)) {
      Swal.fire({
        icon: 'error',
        title: 'Oops...',
        text: `Please reselect user from the list.`
      });
      return;
    }

    try {
      // Upload file and format attachment for message if file exists
      const getAttachments = async () => {
        if (!isNil(fileToUpload)) {
          const response = await uploadFile({
            type: fileToUpload.type,
            data: fileToUpload.data,
            userId: userInfo.id,
            filename: fileToUpload.filename
          });
          if (!isNil(response.data)) {
            const dimensions = await getImageDimensions(fileToUpload);
            const attachment = {
              type: getAttachmentType(fileToUpload.type),
              contentType: fileToUpload.type,
              key: response.data.key,
              filename: response.data.filename,
              thumbnailWidth: response.data.thumbnailWidth,
              ...(dimensions ? { ...dimensions } : {})
            };
            return [attachment];
          }
          return [];
        }
        return [];
      };
      const attachments = await getAttachments();

      const isInternalOnly =
        isMessageEmpty || isTaxExpert || bookkeeperOptions.some((option) => message.includes(`@${option.label}`));

      if (isInternalOnly) {
        const payload = {
          userId: userInfo.id,
          text: `[Internal only] ${message || 'User good to go'}`,
          attachments,
          mentionedBookkeeperIds
        };
        sendInternalMessage(payload);
      } else {
        const payload = {
          userId: userInfo.id,
          text: message,
          attachments
        };
        sendMessage(payload);
      }
    } catch (e) {
      captureException(e);
      Swal.fire({ type: 'error', title: 'Oops...', text: e });
    } finally {
      setIsMessageSending(false);
      setStagedFile(null);
      clear();
      setMessage('');
    }
  };

  if (isUserInfoLoading) {
    return null;
  }

  return (
    <>
      <MacrosModal
        setMacroMessage={(macroMessage) => {
          setMessage(message + ' ' + macroMessage);
        }}
        isOpen={isMacroModalOpen}
        setIsOpen={setIsMacroModalOpen}
      />
      {stagedFile && (
        <ChatSectionFilePreview file={stagedFile} type={getAttachmentType(stagedFile?.type)} onClose={onClose} />
      )}
      <Stack
        direction='row'
        sx={{
          width: '100%',
          mt: 1,
          alignItems: 'center',
          gap: 1
        }}>
        <IconButton onClick={openFileSelector}>
          <img alt='send' src={addIcon} style={{ filter: 'brightness(0)' }} />
        </IconButton>
        <TextField
          fullWidth
          variant='outlined'
          multiline
          minRows={1}
          maxRows={10}
          onChange={(e) => handleSetMessage(e.target.value)}
          placeholder='Reply to user'
          value={message}
          autoComplete='off'
          inputRef={textFieldInputRef}
          slotProps={{
            input: {
              sx: { borderRadius: '12px' },
              endAdornment: (
                <InputAdornment position='end'>
                  <IconButton
                    onClick={(e) => {
                      handleSendChatMessage(e, userInfo.phone, false);
                    }}
                    disabled={isSendDisabled}>
                    <img
                      alt='send'
                      src={isMessageEmpty && !isTaxExpert ? dismissIcon : sendIcon}
                      style={{
                        filter: 'brightness(0)',
                        opacity: isSendDisabled ? 0.5 : 1
                      }}
                    />
                  </IconButton>
                  <IconButton
                    onClick={() => {
                      setIsMacroModalOpen(true);
                    }}>
                    <img alt='macros' src={flashIcon} style={{ filter: 'brightness(0)' }} />
                  </IconButton>
                </InputAdornment>
              )
            }
          }}
        />
        <BookkeeperMentions
          isOpen={isMentionOpen}
          setOpen={setIsMentionOpen}
          anchorEl={textFieldInputRef.current}
          onSetMention={(option) => setMessage(message + option.label + ' ')}
        />
      </Stack>
    </>
  );
};

export default ChatSectionFooter;
